import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Transcription, ActionItem } from '@/types/transcription';
import { toast } from '@/hooks/use-toast';

export const useTranscriptions = () => {
  const queryClient = useQueryClient();

  const { data: transcriptions = [], isLoading, refetch } = useQuery({
    queryKey: ['transcriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transcriptions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []).map(item => ({
        ...item,
        action_items: (Array.isArray(item.action_items) ? item.action_items : []) as unknown as ActionItem[],
      })) as Transcription[];
    },
  });

  const createTranscription = useMutation({
    mutationFn: async ({ filename, storagePath }: { filename: string; storagePath: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('transcriptions')
        .insert({
          filename,
          storage_path: storagePath,
          tenant_id: user?.id,
          status: 'uploading',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptions'] });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij aanmaken",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateTranscription = useMutation({
    mutationFn: async ({
      id,
      title,
      conversation_date,
      extra_info,
    }: {
      id: string;
      title?: string;
      conversation_date?: string | null;
      extra_info?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('transcriptions')
        .update({
          title,
          conversation_date,
          extra_info,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptions'] });
      toast({
        title: "Opgeslagen",
        description: "Transcriptie gegevens zijn bijgewerkt.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij opslaan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const startProcessing = async (transcriptionId: string, storagePath: string) => {
    const { error } = await supabase.functions.invoke('transcribe-audio', {
      body: { transcriptionId, storagePath },
    });
    
    if (error) {
      console.error('Failed to start processing:', error);
      toast({
        title: "Fout bij starten verwerking",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteTranscription = useMutation({
    mutationFn: async (id: string) => {
      // Get the transcription to find storage path
      const transcription = transcriptions.find(t => t.id === id);
      
      // Delete from storage if path exists
      if (transcription?.storage_path) {
        await supabase.storage
          .from('audio-files')
          .remove([transcription.storage_path]);
      }
      
      // Delete from database
      const { error } = await supabase
        .from('transcriptions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptions'] });
      toast({
        title: "Transcriptie verwijderd",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij verwijderen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const retryAnalysis = useMutation({
    mutationFn: async (transcriptionId: string) => {
      const { error } = await supabase.functions.invoke('reanalyze-transcription', {
        body: { transcriptionId },
      });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcriptions'] });
      toast({
        title: "Analyse gestart",
        description: "De transcriptie wordt opnieuw geanalyseerd.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Analyse mislukt",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    transcriptions,
    isLoading,
    refetch,
    createTranscription,
    updateTranscription,
    startProcessing,
    deleteTranscription,
    retryAnalysis,
  };
};
